import baseConfiguratorReducer from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Reducer';
import * as baseConfiguratorActions from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import { actionTypes } from './Actions';
import { ContainerItem } from '../../Types/ContainerItem';
import { ContainerTypeComponentIdentifier } from '../../Constants/Configurator';
import isEqual from 'lodash/isEqual';

function prepareContainers(
  baseContainer: { floors?: number; rows?: number; columns?: number } = {},
  defaultContainerTypeIdentifier = ''
) {
  const { floors = 1, rows = 1, columns = 1 } = baseContainer;
  const containers: ContainerItem[] = [];
  for (let floor = 0; floor < floors; floor++) {
    for (let row = 0; row < rows; row++) {
      for (let column = 0; column < columns; column++) {
        containers.push({
          selectedOption: defaultContainerTypeIdentifier,
          floor,
          row,
          column,
          rightWall: true,
          leftWall: true,
        });
      }
    }
  }
  return containers;
}

function configuratorReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_CONTAINER_ROWS_COLS: {
      const baseContainer = {
        ...state.configuration.customdata.baseContainer,
        rows: action.rows,
        columns: action.cols,
      };
      return {
        ...state,
        configuration: {
          ...state.configuration,
          customdata: {
            ...state.configuration.customdata,
            baseContainer,
          },
        },
      };
    }
    case actionTypes.SET_CONTAINER_FLOORS: {
      const baseContainer = {
        ...state.configuration.customdata.baseContainer,
        floors: action.floors,
      };
      return {
        ...state,
        configuration: {
          ...state.configuration,
          customdata: {
            ...state.configuration.customdata,
            baseContainer,
          },
        },
      };
    }
    case actionTypes.SET_SELECTED_CONTAINER_ITEM: {
      return {
        ...state,
        selectedContainerItem: action.containerItem,
      };
    }
    case actionTypes.UPDATE_SELECTED_CONTAINER_ITEM: {
      const containers = [...state.configuration.containers];

      const currentItem = state.selectedContainerItem;
      const containerItemIndex = containers.findIndex(
        (c) => c.floor === currentItem.floor && c.row === currentItem.row && c.column === currentItem.column
      );

      if (containerItemIndex === -1) {
        return state;
      }

      const newContainerItem = {
        ...state.selectedContainerItem,
        ...action.newContainerItem,
      };
      containers[containerItemIndex] = newContainerItem;

      const isRotated = newContainerItem.row === 1;

      if (newContainerItem.leftWall !== currentItem.leftWall) {
        const targetColDelta = isRotated ? 1 : -1;
        const neighborCol = newContainerItem.column + targetColDelta;

        const neighborIndex = containers.findIndex(
          (c) =>
            c.floor === newContainerItem.floor && c.row === newContainerItem.row && c.column === neighborCol
        );

        if (neighborIndex !== -1) {
          containers[neighborIndex] = {
            ...containers[neighborIndex],
            rightWall: newContainerItem.leftWall,
          };
        }
      }

      if (newContainerItem.rightWall !== currentItem.rightWall) {
        const targetColDelta = isRotated ? -1 : 1;
        const neighborCol = newContainerItem.column + targetColDelta;

        const neighborIndex = containers.findIndex(
          (c) =>
            c.floor === newContainerItem.floor && c.row === newContainerItem.row && c.column === neighborCol
        );

        if (neighborIndex !== -1) {
          containers[neighborIndex] = {
            ...containers[neighborIndex],
            leftWall: newContainerItem.rightWall,
          };
        }
      }

      return {
        ...state,
        configuration: {
          ...state.configuration,
          containers,
        },
        selectedContainerItem: newContainerItem,
      };
    }
    case baseConfiguratorActions.RECEIVE_OPTIONS: {
      const nextState = baseConfiguratorReducer(state, action);
      if (nextState.selectedoptionclassification?.identifier === ContainerTypeComponentIdentifier) {
        return {
          ...nextState,
          defaultContainerTypeIdentifier: nextState.options[0]?.identifier,
        };
      }
      return nextState;
    }
    case actionTypes.RECEIVE_CONFIGURATION: {
      const { configuration } = action;
      return {
        ...state,
        configuration: configuration,
      };
    }

    case actionTypes.RECEIVE_SAVE_CONFIGURATION: {
      const { configuration } = action;
      const { code } = configuration;
      return {
        ...state,
        configuration,
        loadCode: code,
      };
    }

    case actionTypes.RESET_CODE: {
      return {
        ...state,
        configuration: {
          ...state.configuration,
          code: null,
        },
      };
    }

    case actionTypes.SET_LOAD_CODE: {
      const { loadCode } = action;
      return {
        ...state,
        loadCode,
      };
    }

    case actionTypes.REQUEST_ERROR: {
      const { error } = action;
      return {
        ...state,
        error,
      };
    }

    case actionTypes.RESET_ERROR: {
      return {
        ...state,
        error: null,
      };
    }

    case actionTypes.SET_SELECTED_FLOOR: {
      return {
        ...state,
        selectedFloor: action.floor,
      };
    }

    default: {
      return baseConfiguratorReducer(state, action);
    }
  }
}

const baseInitialState = baseConfiguratorReducer();
const initialState = {
  ...baseInitialState,
  configuration: {
    ...baseInitialState.configuration,
    customdata: {
      ...baseInitialState.customdata,
      baseContainer: {
        floors: 1,
        rows: 1,
        columns: 1,
      },
    },
  },
  selectedContainerItem: null,
};

export default function configuratorReducerWatcher(state = initialState, action) {
  const prevBaseContainer = state.configuration.customdata?.baseContainer;
  const nextState = configuratorReducer(state, action);
  // make sure the baseContainer data is carried over to the received configuration, if it does not have one
  if (prevBaseContainer && !nextState.configuration.customdata?.baseContainer) {
    if (!nextState.configuration.customdata) {
      nextState.configuration.customdata = {};
    }
    nextState.configuration.customdata.baseContainer = prevBaseContainer;
  }
  const baseContainer = nextState.configuration.customdata?.baseContainer;

  const { defaultContainerTypeIdentifier } = nextState;
  if (baseContainer && defaultContainerTypeIdentifier) {
    const isLoadAction =
      action.type === actionTypes.RECEIVE_CONFIGURATION ||
      action.type === actionTypes.RECEIVE_SAVE_CONFIGURATION;

    if (
      (!isLoadAction && !isEqual(baseContainer, prevBaseContainer)) ||
      !nextState.configuration.containers?.length
    ) {
      nextState.configuration.containers = prepareContainers(baseContainer, defaultContainerTypeIdentifier);
    }
  }
  if (baseContainer && baseContainer.floors !== prevBaseContainer.floors) {
    nextState.selectedFloor = baseContainer.floors - 1;
  }

  return nextState;
}
